import type { QuestionDetailRead, QuestionProfileRead, QuestionRead } from '@models/api/question';
import type { QuestionCacheDetail, QuestionCacheItem } from './types';

type AnyQuestionShape = QuestionRead | QuestionDetailRead | QuestionProfileRead;

/**
 * Normalise any API question shape into the unified QuestionCacheItem.
 * The `detail` sub-object is only populated for shapes carrying `status`
 * (or partially, for QuestionProfileRead's `asked_for_user`) — an omitted
 * `detail` field stays undefined so spread-merging never erases a richer
 * `detail` already in the cache. The base fields above `detail` are required
 * on every input shape (see question.ts:148-157/188-204/242-246), so they are
 * always real values under a conformant payload — never undefined.
 */
export const toQuestionCacheItem = (q: AnyQuestionShape): QuestionCacheItem => {
  const base: QuestionCacheItem = {
    id: q.id,
    slug: q.slug,
    title: q.title,
    content: q.content,
    created_at: q.created_at,
    event_summaries: q.event_summaries,
    space_summaries: q.space_summaries,
    uplifts_count: q.uplifts_count,
    me_too_count: q.me_too_count,
    i_can_help_count: q.i_can_help_count,
    is_anonymous: q.is_anonymous,
    is_featured: q.is_featured,
    is_official: q.is_official,
    visibility: q.visibility,
    is_my_question: q.is_my_question,
    has_extra_options: q.has_extra_options,
    user_has_me_too: q.user_has_me_too,
    user_has_i_can_help: q.user_has_i_can_help,
    user_has_bookmarked: q.user_has_bookmarked,
    claim_status: q.claim_status,
    created_by_user: q.created_by_user,
    asked_for_user_id: q.asked_for_user_id,
  };

  // Copied only when the response carries it (TECH-197 BE-1d): an older shape has no `kind`, and
  // writing `undefined` would erase a known kind under the upsert's spread-merge.
  if (q.kind !== undefined) {
    base.kind = q.kind;
  }
  // The poll block the same way: a response without it leaves a known block alone.
  if (q.poll !== undefined) {
    base.poll = q.poll;
  }

  if ('tag_name' in q) {
    base.tag_name = (q as QuestionRead).tag_name;
  }

  // Held posts: copy held_spaces only when the wire value is a list. The backend sends one to
  // the viewer it returns the list to (on the detail, a create or edit response, a profile
  // list), and [] is the authoritative "nothing held". Every other response, and every other
  // viewer, gets null: "this response does not know", which must leave the entity's known list
  // alone under the spread-merge.
  if (Array.isArray(q.held_spaces)) {
    base.held_spaces = q.held_spaces;
  }

  // QuestionProfileRead carries asked_for_user but no space_id, so the detail
  // block below never runs for profile shapes. Seed a partial detail so profile
  // cards can render the beneficiary name without a full list/detail fetch.
  if (!('status' in q) && 'asked_for_user' in q) {
    base.detail = { asked_for_user: q.asked_for_user } as QuestionCacheDetail;
  }

  if ('status' in q) {
    const read = q;
    base.detail = {
      user: read.user ?? null,
      event: 'event' in read ? read.event : null,
      event_ids: 'event_ids' in read ? read.event_ids : null,
      space_id: read.space_id,
      space_ids: 'space_ids' in read ? read.space_ids : null,
      space: read.space,
      user_id: 'user_id' in read ? read.user_id : null,
      status: read.status,
      pinned_at: read.pinned_at,
      pinned_by_user_id: read.pinned_by_user_id,
      pinned_by_name: read.pinned_by_name,
      topic_id: read.topic_id,
      topic_name: read.topic_name,
      asked_for_self: read.asked_for_self,
      asked_for_email: read.asked_for_email,
      asked_for_user: read.asked_for_user,
      created_by_user_id: read.created_by_user_id,
      // Async-matching UX (issue #22): present on both QuestionRead and QuestionDetailRead
      // (unlike author_live_event etc. below), so no per-shape guard is needed.
      match_status: read.match_status,
      pool_is_thin: read.pool_is_thin,
      ...('author_live_event' in read && { author_live_event: read.author_live_event }),
      ...('updated_at' in read && { updated_at: read.updated_at }),
      ...('archived_at' in read && { archived_at: read.archived_at }),
      ...('pre_deletion_status' in read && {
        pre_deletion_status: read.pre_deletion_status,
      }),
    };
  }

  return base;
};
